import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Trophy, Loader2, Search, Film, Flame, Download } from "lucide-react";
import { getTopClips, downloadTopUrl } from "../lib/api";
import { ClipCard } from "../components/ClipCard";
import { ProDownloadLink } from "../components/UpgradeModal";
import TopPerformers from "../components/TopPerformers";

export default function BestOfView() {
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [minScore, setMinScore] = useState(0);
  const [videoOnly, setVideoOnly] = useState(false);
  const [days, setDays] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getTopClips({ q, min_score: minScore, kind: videoOnly ? "video" : "all", limit: 120, days });
      setClips(data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [q, minScore, videoOnly, days]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="bestof-view" className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-border pb-6">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary">Cross-Show Ranking</p>
          <h2 className="mt-1 flex items-center gap-3 font-display text-4xl tracking-wide text-white sm:text-5xl">
            <Trophy className="h-8 w-8 text-accent" /> BEST OF BARTH
          </h2>
          <p className="mt-2 font-body text-sm text-muted-foreground">
            Your hardest-hitting clips from the entire archive, ranked by score. No show-hopping.
          </p>
        </div>
        <ProDownloadLink
          data-testid="download-top10"
          reason="Best-Of bulk download is a Pro feature. Upgrade to export your top reel."
          href={downloadTopUrl({ limit: 10, min_score: minScore, kind: videoOnly ? "video" : "all" })}
          className="flex h-11 items-center gap-2 bg-accent px-5 text-xs font-semibold uppercase tracking-[0.15em] text-accent-foreground transition-colors hover:bg-accent/90"
        >
          <Download className="h-4 w-4" /> Download Top 10
        </ProDownloadLink>
      </div>

      <TopPerformers />

      {/* Time range (weekly digest) */}
      <div className="flex flex-wrap items-center gap-2">
        {[{ v: 0, l: "All Time" }, { v: 7, l: "This Week" }, { v: 30, l: "Last 30 Days" }].map((r) => (
          <button
            key={r.v}
            data-testid={`bestof-range-${r.v}`}
            onClick={() => setDays(r.v)}
            className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors ${
              days === r.v ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-white"
            }`}
          >
            {r.l}
          </button>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-4 border border-border bg-card/60 p-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="bestof-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search titles, captions, transcript…"
            className="w-full rounded-none border border-border bg-background py-2.5 pl-10 pr-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
            <Flame className="h-3.5 w-3.5 text-primary" /> min {minScore}
          </span>
          <input
            data-testid="bestof-minscore"
            type="range" min="0" max="95" step="5"
            value={minScore}
            onChange={(e) => setMinScore(parseInt(e.target.value))}
            className="w-40 accent-[#CCFF00]"
          />
        </div>
        <button
          data-testid="bestof-video-toggle"
          onClick={() => setVideoOnly((v) => !v)}
          className={`flex items-center gap-1.5 border px-3 py-2 text-[11px] uppercase tracking-widest transition-colors ${
            videoOnly ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground hover:text-white"
          }`}
        >
          <Film className="h-3.5 w-3.5" /> Video only
        </button>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : clips.length === 0 ? (
        <p className="py-20 text-center text-muted-foreground">No clips match — loosen the filters or wait for more shows to finish mining.</p>
      ) : (
        <>
          <p data-testid="bestof-count" className="font-mono text-xs text-muted-foreground">{clips.length} clips ranked</p>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
            {clips.map((c, i) => (
              <div key={c.id} className="relative">
                <div className="absolute -left-2 -top-2 z-20 grid h-9 w-9 place-items-center bg-primary font-display text-lg text-primary-foreground shadow-lg">
                  {i + 1}
                </div>
                <ClipCard clip={c} show={c.show} index={i} onUpdated={load} sourceLabel={c.show?.title} />
              </div>
            ))}
          </div>
        </>
      )}
    </motion.div>
  );
}
